export class StorageState<Value> {
	private readonly storage?: Storage;
	private storedValue = $state<Value>();
	public loaded = $state(false);

	constructor(
		storageKind: "local" | "session",
		private readonly storageKey: string,
		private readonly initialValue: Value,
		private readonly reviver?: (this: void, key: string, value: unknown) => unknown,
	) {
		this.storedValue = structuredClone(initialValue);

		if (typeof window === "undefined") {
			return;
		}
		this.storage = window[`${storageKind}Storage`];

		this.resyncFromStorage();

		// To handle a deeply-reactive values change,
		// we need to use $effect to watch for changes and save them to storage.

		this.dispose = $effect.root(() => {
			const storageListener = this.handleStorageEvent.bind(this);
			window.addEventListener("storage", storageListener);

			const visibilityListener = this.handleVisibilityChange.bind(this);
			document.addEventListener("visibilitychange", visibilityListener);

			// A page restored from the back/forward cache doesn't necessarily fire "visibilitychange" -
			// "pageshow" with persisted=true is the dedicated signal for that.
			const pageshowListener = this.handlePageshow.bind(this);
			window.addEventListener("pageshow", pageshowListener);

			$effect(() => {
				this.saveValueToStorage(this.value);
			});

			return (): void => {
				window.removeEventListener("storage", storageListener);
				document.removeEventListener("visibilitychange", visibilityListener);
				window.removeEventListener("pageshow", pageshowListener);
			};
		});

		this.loaded = true;
	}

	public dispose(): void {
		// Set in constructor
	}

	private resyncFromStorage(): void {
		if (!this.storage) {
			return;
		}
		const storedValue = this.storage.getItem(this.storageKey);
		if (storedValue === null) {
			// Another tab removed the key (or cleared storage entirely) - follow suit.
			this.storedValue = structuredClone(this.initialValue);
			return;
		}
		try {
			this.storedValue = JSON.parse(storedValue, this.reviver) as Value;
		} catch {
			// Corrupted/unparsable value should never happen, but if it does, don't get stuck on it.
			this.storedValue = structuredClone(this.initialValue);
		}
	}

	private handleStorageEvent(event: StorageEvent): void {
		if (event.storageArea !== this.storage || (event.key !== this.storageKey && event.key !== null)) {
			return;
		}
		this.resyncFromStorage();
	}

	private handleVisibilityChange(): void {
		if (document.visibilityState !== "visible") {
			return;
		}
		this.resyncFromStorage();
	}

	private handlePageshow(event: PageTransitionEvent): void {
		if (!event.persisted) {
			return;
		}
		this.resyncFromStorage();
	}

	private saveValueToStorage(value: Value): void {
		if (value === null || value === undefined) {
			this.storage?.removeItem(this.storageKey);
		} else {
			this.storage?.setItem(this.storageKey, JSON.stringify(value));
		}
	}

	public get value(): Value {
		return this.storedValue as Value;
	}

	public set value(newValue: Value) {
		this.storedValue = newValue;
	}
}
